import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { customersService, transactionsService } from '../services/api';
import creditMemoService from '../services/api/creditMemoService';
import { formatCurrency } from '../utils/invoiceHelpers';
import { getCustomerDisplayName } from '../utils/customerHelpers';
import { US_STATES } from '../utils/usStates';
import {
  formatCityName,
  formatZipCode,
  formatStreetAddress
} from '../utils/addressFormatters';
import StatusBadge from './shared/StatusBadge';
import PermissionGate from './shared/PermissionGate';
import CreateCreditMemoModal from './creditMemos/CreateCreditMemoModal';
import CreditMemoDetailsModal from './creditMemos/CreditMemoDetailsModal';
import { PERMISSIONS } from '../constants/permissionMappings';
import { usePermissions } from '../contexts/PermissionContext';
import StoredCardsSection from './customers/StoredCardsSection';

function CustomerDetails() {
  const { customerId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  
  const { hasPermission } = usePermissions();

  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [arSummary, setArSummary] = useState(null);
  const [balanceHistory, setBalanceHistory] = useState(null);
  const [showBalanceHistory, setShowBalanceHistory] = useState(false);
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionMenuRef = useRef(null);
  const [customerCredit, setCustomerCredit] = useState(null);
  const [customerCreditMemos, setCustomerCreditMemos] = useState([]);
  const [showCreditMemosSection, setShowCreditMemosSection] = useState(false);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState(null);
  const [showCreditMemoDetails, setShowCreditMemoDetails] = useState(false);
  const [showCreateCreditMemo, setShowCreateCreditMemo] = useState(false);
  const [formData, setFormData] = useState({

    company: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Define fetchCustomerDetails as a useCallback function
  const fetchCustomerDetails = useCallback(async () => {
    if (!user?.id || !customerId) return;
    
    try {
      setLoading(true);
      
      // Use our service methods - no auth headers needed
      const [customerResponse, transactionsResponse] = await Promise.all([
        customersService.getCustomerDetails(customerId),
        transactionsService.getCustomerTransactions(customerId)
      ]);
      
      console.log("Customer data from API:", customerResponse);
      
      // Services now return data directly
      const customer = customerResponse.customer || customerResponse;
      const transactions = transactionsResponse || [];
      
      setCustomerData({
        customer,
        transactions
      });
      
      // Fetch AR summary
      try {
        const arData = await customersService.getCustomerArSummary(customerId);
        setArSummary(arData);
      } catch (arError) {
        console.error('Failed to fetch AR summary:', arError);
        // Don't fail the whole page if AR data is unavailable
      }
      
      setFormData({
        company: customer.company || '',
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || '',
        notes: customer.notes || ''
      });
    } catch (err) {
      console.error('Failed to fetch customer details:', err);
      setError('Failed to fetch customer details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, customerId]);
  
  // Fetch customer details on mount
  useEffect(() => {
    fetchCustomerDetails();
  }, [fetchCustomerDetails]);

  // Fetch customer credit balance and memos
  const loadCustomerCreditData = useCallback(async () => {
    if (!customerId) return;
    try {
      const [creditResult, memosResult] = await Promise.all([
        creditMemoService.getCustomerCredit(customerId),
        creditMemoService.getCustomerCreditMemos(customerId, { includeApplied: true, includeVoid: true })
      ]);
      if (creditResult.success) {
        setCustomerCredit(creditResult);
      }
      if (memosResult.success) {
        setCustomerCreditMemos(memosResult.creditMemos || []);
      }
    } catch (err) {
      console.error('Error loading customer credit:', err);
    }
  }, [customerId]);

  useEffect(() => {
    loadCustomerCreditData();
  }, [loadCustomerCreditData]);
  
  // Function to display customer name (company name or first + last name)
  // Use centralized customer display logic
  const getCustomerDisplayNameForHeader = (customer) => {
    const displayName = getCustomerDisplayName(customer);
    return displayName !== 'Guest' ? displayName : '';
  };
  
  // Handle form input changes with real-time formatting where appropriate
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case 'address':
        formattedValue = value.slice(0, 100);
        break;
      case 'city':
        formattedValue = value.slice(0, 50);
        break;
      case 'zipCode':
        formattedValue = formatZipCode(value);
        break;
      default:
        formattedValue = value;
    }

    setFormData({
      ...formData,
      [name]: formattedValue
    });

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Apply final formatting on blur (avoids interfering while user is typing)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    switch (name) {
      case 'address':
        finalValue = formatStreetAddress(value);
        break;
      case 'city':
        finalValue = formatCityName(value);
        break;
      case 'zipCode':
        finalValue = formatZipCode(value);
        break;
      default:
        break;
    }

    if (finalValue !== value) {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Require either company name or first name + last name
    if (!formData.company && (!formData.firstName || !formData.lastName)) {
      if (!formData.company) {
        newErrors.company = "Company name is required if individual name is not provided";
      }
      if (!formData.firstName) {
        newErrors.firstName = "First name is required if company name is not provided";
      }
      if (!formData.lastName) {
        newErrors.lastName = "Last name is required if company name is not provided";
      }
      isValid = false;
    }
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
      isValid = false;
    }
    
    // Phone validation (allow common formatting characters)
    const normalizedPhone = formData.phone.replace(/[\s.]/g, '');
    if (formData.phone && !/^[0-9+\-()]{10,15}$/.test(normalizedPhone)) {
      newErrors.phone = "Phone number is invalid";
      isValid = false;
    }
    
    setFormErrors(newErrors);
    return isValid;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    console.log("Before submission - formData:", formData);
    
    try {
      setSaving(true);

      // Use our service method - no auth header needed
      const response = await customersService.updateCustomer(customerId, formData);
      
      console.log("API Response:", response);
      
      // Update customer data with the response
      setCustomerData({
        ...customerData,
        customer: response
      });
      
      // Also update formData to ensure consistency
setFormData({
  company: response.company || '',
  firstName: response.firstName || '',
  lastName: response.lastName || '',
  email: response.email || '',
  phone: response.phone || '',
  address: response.address || '',
  city: response.city || '',
  state: response.state || '',
  zipCode: response.zipCode || '',
  notes: response.notes || ''
});
      
      setShowEditForm(false);
      setError(null);
    } catch (err) {
      console.error('Failed to update customer:', err);
      const apiMessage = err.response?.data?.error?.message || err.response?.data?.error;
      setError(
        typeof apiMessage === 'string'
          ? apiMessage
          : 'Failed to update customer. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };
  
  const copyPortalLink = async () => {
    try {
      const result = await customersService.generatePortalLink(customerId);
      await navigator.clipboard.writeText(result.portalUrl);
      setPortalLinkCopied(true);
      setTimeout(() => setPortalLinkCopied(false), 3000);
    } catch (error) {
      console.error('Failed to generate portal link:', error);
      setError('Failed to generate portal link. Please try again.');
    }
  };

  // Handle click outside action menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setIsActionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle customer deletion
  const handleDeleteCustomer = async () => {
    if (!customerId) return;

    try {
      setDeleteLoading(true);
      setError(null);

      await customersService.deleteCustomer(customerId);

      // Navigate back to customers list after successful deletion
      navigate('/merchant/customers', {
        state: { message: 'Customer deleted successfully' }
      });
    } catch (err) {
      console.error('Failed to delete customer:', err);
      // Check for specific error messages from backend
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to delete customer. Please try again.');
      }
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && !customerData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }
  
  if (error && !customerData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 text-red-800 p-4 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="bg-brand text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-hover transition-colors"
        >
          Back to Customers
        </button>
      </div>
    );
  }

  if (!customerData || !customerData.customer) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 text-red-800 p-4 rounded mb-4">
          Customer not found
        </div>
        <button
          onClick={() => navigate(-1)}
          className="bg-brand text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-hover transition-colors"
        >
          Back to Customers
        </button>
      </div>
    );
  }
  
  const { customer, transactions } = customerData;
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800 flex items-center mb-2"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Customers
          </button>
          <h1 className="text-3xl font-bold">{getCustomerDisplayNameForHeader(customer)}</h1>
        </div>
        {!showEditForm && (
          <div className="flex items-center space-x-2">
            {/* Customer Portal Link */}
            <button
              onClick={copyPortalLink}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              title="Copy customer portal link"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              <span className="text-sm font-medium">Customer Portal</span>
            </button>
            
            {/* Create Credit Button */}
            <PermissionGate permission={PERMISSIONS.credit_memos.create}>
              <button
                onClick={() => setShowCreateCreditMemo(true)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                title="Create credit for this customer"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">Create Credit</span>
              </button>
            </PermissionGate>

            {/* Edit Customer Button */}
            <PermissionGate permission={PERMISSIONS.customers.edit}>
              <button
                onClick={() => setShowEditForm(true)}
                className="bg-brand text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-hover transition-colors"
              >
                Edit Customer
              </button>
            </PermissionGate>

            {/* 3-dot Action Menu - Only show if user has delete permission */}
            {hasPermission(PERMISSIONS.customers.delete) && (
              <div className="relative" ref={actionMenuRef}>
                <button
                  onClick={() => setIsActionMenuOpen(!isActionMenuOpen)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="More actions"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                {isActionMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setIsActionMenuOpen(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Delete Customer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <div className="bg-red-100 text-red-800 p-4 rounded mb-4">
          {error}
        </div>
      )}
      
      {portalLinkCopied && (
        <div className="bg-blue-100 text-blue-800 p-4 rounded mb-4">
          Portal link copied to clipboard!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Customer</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{customer?.company || `${customer?.firstName} ${customer?.lastName}`}</strong>?
              This action cannot be undone and will permanently remove the customer record.
            </p>
            {arSummary && arSummary.outstanding_balance > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This customer has an outstanding balance of {formatCurrency(arSummary.outstanding_balance)}.
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCustomer}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditForm ? (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Edit Customer</h2>
            <button 
              onClick={() => setShowEditForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={formData.company || ''}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${formErrors.company ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Company Name"
              />
              {formErrors.company && (
                <p className="text-red-500 text-sm mt-1">{formErrors.company}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="First Name"
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Last Name"
                />
                {formErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Email"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Phone"
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Street Address</label>
              <input
                type="text"
                name="address"
                value={formData.address || ''}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">State</label>
                <select
                  name="state"
                  value={formData.state || ''}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select</option>
                  {US_STATES.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength="10"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="12345"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg"
                rows="3"
                placeholder="Additional Notes"
              ></textarea>
            </div>
            
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-brand text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="flex-1 bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
          </div>
      ) : (
        <>
          {/* AR Summary Card */}
          {arSummary && arSummary.outstanding_balance > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-lg mb-6 border-l-4 border-brand">
              <h2 className="text-xl font-semibold mb-4">Accounts Receivable Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(arSummary.outstanding_balance)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Invoiced</p>
                  <p className="text-lg font-semibold">{formatCurrency(arSummary.total_invoiced)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-lg font-semibold text-green-600">{formatCurrency(arSummary.total_paid)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Open Invoices</p>
                  <p className="text-lg font-semibold">{arSummary.open_invoices}</p>
                </div>
              </div>
              
              {arSummary.oldest_due_date && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Oldest Due Date</p>
                      <p className="text-base font-medium text-red-600">
                        {format(new Date(arSummary.oldest_due_date), 'MMM d, yyyy')}
                        {new Date(arSummary.oldest_due_date) < new Date() && (
                          <StatusBadge type="invoice_status" value={{paymentStatus: 'unpaid', status: 'sent', dueDate: arSummary.oldest_due_date}} size="xs" />
                        )}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const history = await customersService.getCustomerBalanceHistory(customerId);
                          setBalanceHistory(history);
                          setShowBalanceHistory(!showBalanceHistory);
                        } catch (err) {
                          console.error('Failed to fetch balance history:', err);
                        }
                      }}
                      className="text-sm text-brand hover:text-brand-hover font-medium"
                    >
                      {showBalanceHistory ? 'Hide' : 'View'} Balance History
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Customer Credits Section */}
          {(customerCredit || customerCreditMemos.length > 0) && (
            <div className="bg-white p-6 rounded-lg shadow-lg mb-6 border-l-4 border-green-400">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold">Customer Credits</h2>
                  {customerCredit?.totalAvailableCreditCents > 0 && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      {formatCurrency(customerCredit.totalAvailableCreditCents / 100)} available
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {customerCreditMemos.length > 0 && (
                    <button
                      onClick={() => setShowCreditMemosSection(!showCreditMemosSection)}
                      className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center gap-1"
                    >
                      {showCreditMemosSection ? 'Hide' : 'View'} Details
                      <svg
                        className={`w-4 h-4 transition-transform ${showCreditMemosSection ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                  <PermissionGate permission={PERMISSIONS.credit_memos.create}>
                    <button
                      onClick={() => setShowCreateCreditMemo(true)}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      + Add Credit
                    </button>
                  </PermissionGate>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Available Balance</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency((customerCredit?.totalAvailableCreditCents || 0) / 100)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Memos</p>
                  <p className="text-lg font-semibold">{customerCredit?.activeCreditMemoCount || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Issued</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency((customerCredit?.totalCreditIssuedCents || 0) / 100)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Applied</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency((customerCredit?.totalAppliedCreditCents || 0) / 100)}
                  </p>
                </div>
              </div>

              {/* Credit Memos Table (Expandable) */}
              {showCreditMemosSection && customerCreditMemos.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Credit Memo History</h3>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Memo #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {customerCreditMemos.map((memo) => (
                          <tr key={memo.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">
                              {memo.creditMemoNumber}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {format(new Date(memo.createdAt), 'MMM d, yyyy')}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500 capitalize">
                              {memo.sourceType?.replace('_', ' ') || 'Manual'}
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-medium">
                              {formatCurrency(memo.amountCents / 100)}
                            </td>
                            <td className={`px-4 py-2 text-sm text-right font-medium ${
                              memo.remainingBalanceCents > 0 ? 'text-green-600' : 'text-gray-400'
                            }`}>
                              {formatCurrency(memo.remainingBalanceCents / 100)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                memo.status === 'active' ? 'bg-green-100 text-green-800' :
                                memo.status === 'partially_applied' ? 'bg-yellow-100 text-yellow-800' :
                                memo.status === 'applied' ? 'bg-blue-100 text-blue-800' :
                                memo.status === 'void' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {memo.status === 'partially_applied' ? 'Partial' :
                                 memo.status === 'applied' ? 'Applied' :
                                 memo.status === 'void' ? 'Void' :
                                 memo.status === 'active' ? 'Active' : memo.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => {
                                  setSelectedCreditMemo(memo);
                                  setShowCreditMemoDetails(true);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* No credits message */}
              {customerCreditMemos.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No credit memos have been issued for this customer yet.
                </p>
              )}
            </div>
          )}

          {/* Balance History */}
          {showBalanceHistory && balanceHistory && (
            <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
              <h3 className="text-lg font-semibold mb-4">Balance History</h3>
              <div className="max-h-64 overflow-y-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {balanceHistory.history.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {format(new Date(entry.created_at), 'MMM d, yyyy')}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <StatusBadge 
                            type="transaction_type" 
                            value={entry.transaction_type}
                            size="xs"
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">{entry.description}</td>
                        <td className={`px-4 py-2 text-sm text-right font-medium ${
                          entry.transaction_type === 'invoice' ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {entry.transaction_type === 'invoice' ? '+' : '-'}{formatCurrency(entry.amount)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-bold">
                          {formatCurrency(entry.balance_after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
              
              {customer.company && (
                <div className="mb-3">
                  <p className="text-sm text-gray-500">Company Name</p>
                  <p className="text-lg">{customer.company}</p>
                </div>
              )}
              
              {(customer.firstName || customer.lastName) && (
                <div className="mb-3">
                  <p className="text-sm text-gray-500">Contact Name</p>
                  <p className="text-lg">{`${customer.firstName || ''} ${customer.lastName || ''}`.trim()}</p>
                </div>
              )}
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-lg">{customer.email || '-'}</p>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-lg">{customer.phone || '-'}</p>
              </div>
            </div>
            
            <div>
              <h2 className="text-xl font-semibold mb-4">Address</h2>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Street Address</p>
                <p className="text-lg">{customer.address || '-'}</p>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">City, State, ZIP</p>
                <p className="text-lg">
                  {[
                    customer.city || '',
                    customer.state || '',
                    customer.zipCode || ''
                  ].filter(Boolean).join(', ') || '-'}
                </p>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Country</p>
                <p className="text-lg">{customer.country || '-'}</p>
              </div>
            </div>
          </div>
          
          {customer.notes && (
            <div className="mt-4 border-t pt-4">
              <h2 className="text-xl font-semibold mb-2">Notes</h2>
              <p className="text-gray-700 whitespace-pre-line">{customer.notes}</p>
            </div>
          )}
          
          <div className="mt-4 text-sm text-gray-500">
            <p>Customer since {format(new Date(customer.createdAt), 'MMMM d, yyyy')}</p>
            {customer.updatedAt !== customer.createdAt && (
              <p>Last updated {format(new Date(customer.updatedAt), 'MMMM d, yyyy')}</p>
            )}
          </div>
        </div>
        </>
      )}
      
      {/* Payment Methods (Stored Cards) */}
      {customerData?.customer && (
        <StoredCardsSection
          customerId={customerData.customer.id}
          customerData={customerData.customer}
        />
      )}

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-6">Transactions</h2>
        
        {transactions && transactions.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Card Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Entry Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const ref = txn.transactionReference || txn.transactionId || txn.id;
                          if (ref.startsWith('EXT-')) {
                            return `External - ${ref}`;
                          } else if (txn.isLinked || txn.allocatedAmount > 0 || txn.allocated_amount > 0 || txn.invoiceNumber) {
                            return `Linked - ${ref}`;
                          }
                          return ref;
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        {txn.date ? format(new Date(txn.date), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                        {txn.cardType || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          <span className={txn.transactionType === 'refund' ? 'text-orange-600' : 'text-gray-900'}>
                            {formatCurrency(txn.amount || 0)}
                          </span>
                          {txn.refundedAmount > 0 && (
                            <StatusBadge type="payment_status" value="partial" size="xs" />
                          )}
                          {txn.isVoided && (
                            <StatusBadge type="transaction_type" value="void" size="xs" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <StatusBadge type="entry_mode" value={txn.entryMode} size="xs" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                        <StatusBadge 
                          type="transaction_status" 
                          value={{
                            status: txn.status,
                            transactionType: txn.transactionType,
                            isVoided: txn.isVoided
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/merchant/transactions/${txn.transactionReference || txn.transactionId || txn.id}`}
                          className="text-brand hover:text-brand-hover"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
            <svg 
              className="mx-auto h-12 w-12 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              There are no transactions associated with this customer.
            </p>
          </div>
        )}
      </div>

      {/* Create Credit Memo Modal */}
      <CreateCreditMemoModal
        isOpen={showCreateCreditMemo}
        onClose={() => setShowCreateCreditMemo(false)}
        customerId={customerId}
        onSuccess={async () => {
          setShowCreateCreditMemo(false);
          // Refresh customer credit data
          await loadCustomerCreditData();
        }}
      />

      {/* Credit Memo Details Modal */}
      <CreditMemoDetailsModal
        isOpen={showCreditMemoDetails}
        onClose={() => {
          setShowCreditMemoDetails(false);
          setSelectedCreditMemo(null);
        }}
        creditMemo={selectedCreditMemo}
        canVoid={hasPermission(PERMISSIONS.credit_memos.void)}
        onVoidSuccess={async () => {
          setShowCreditMemoDetails(false);
          setSelectedCreditMemo(null);
          // Refresh credit data after voiding
          await loadCustomerCreditData();
        }}
      />
    </div>
  );
}

export default CustomerDetails;